import React, { useState, useMemo } from 'react';
import { Modal } from 'react-bootstrap';
import { ListToolbar, FilterChips } from '../../Common/ListToolbar';
import InfoPopover from '../../Common/InfoPopover';
import Callout from '../../Common/Callout';
import '../Personell/employee-form.css';
import './select-wage-type.css';
import DatePicker from 'react-datepicker';
import { Calculator, Plus } from 'lucide-react';
import { COMPREHENSIVE_WAGE_TYPES } from './wageTypesConfig';
import "react-datepicker/dist/react-datepicker.css";

const SelectWageTypeModal = ({ show, handleClose, handleSelect, wageTypes }) => {
  const [selectedWageTypes, setSelectedWageTypes] = useState([]);
  const [wageTypeQuantities, setWageTypeQuantities] = useState({});
  const [absenceDates, setAbsenceDates] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [showPopularOnly, setShowPopularOnly] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [validationErrors, setValidationErrors] = useState([]);

  // Enhanced wage types configuration with icons and better UX
  // Use comprehensive wage types configuration
  const ESSENTIAL_WAGE_TYPES = COMPREHENSIVE_WAGE_TYPES;

  // Enhanced filtering with search, category, and popularity filters
  const filteredWageTypes = useMemo(() => {
    let filtered = wageTypes.filter(wageType => ESSENTIAL_WAGE_TYPES.hasOwnProperty(wageType.code));

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(wageType =>
        wageType.code.toLowerCase().includes(query) ||
        wageType.description.toLowerCase().includes(query) ||
        ESSENTIAL_WAGE_TYPES[wageType.code].description.toLowerCase().includes(query)
      );
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(wageType =>
        wageType.code.charAt(0) === selectedCategory
      );
    }

    // Filter by popularity
    if (showPopularOnly) {
      filtered = filtered.filter(wageType =>
        ESSENTIAL_WAGE_TYPES[wageType.code].isPopular
      );
    }

    return filtered;
  }, [wageTypes, searchQuery, selectedCategory, showPopularOnly]);

  // Helper function to get input configuration for a wage type
  const getInputConfig = (wageTypeCode) => {
    return ESSENTIAL_WAGE_TYPES[wageTypeCode] || {
      unit: null,
      inputType: 'hours',
      defaultQuantity: 1,
      label: 'Antal',
      requiresAbsenceDates: false,
      icon: Calculator,
      color: '#6b7280',
      description: 'Standard löneart'
    };
  };

  // Quick action handlers
  const handleQuickAction = (wageTypeId, value) => {
    setWageTypeQuantities(prev => ({
      ...prev,
      [wageTypeId]: value
    }));
  };

  // Validation function
  const validateSelection = () => {
    const errors = [];

    selectedWageTypes.forEach(wageType => {
      const config = getInputConfig(wageType.code);
      const quantity = wageTypeQuantities[wageType.id] || config.defaultQuantity;

      // Check if quantity is valid
      if (quantity <= 0) {
        errors.push(`${wageType.code}: Antal måste vara större än 0`);
      }

      // Check absence dates for wage types that require them
      if (config.requiresAbsenceDates) {
        const dates = absenceDates[wageType.id];
        if (!dates || !dates.startDate) {
          errors.push(`${wageType.code} (${config.absenceType}) kräver frånvarodatum enligt svensk lag`);
        }
      }
    });

    setValidationErrors(errors);
    return errors.length === 0;
  };

  // Handle absence date changes
  const handleAbsenceDateChange = (wageTypeId, date) => {
    setAbsenceDates(prev => ({
      ...prev,
      [wageTypeId]: {
        startDate: date
      }
    }));
  };

  // Format wage types for react-select
  const selectOptions = useMemo(() => {
    return filteredWageTypes.map(wageType => ({
      value: wageType.id,
      label: `${wageType.code} - ${wageType.description}`,
      wageType: wageType
    }));
  }, [filteredWageTypes]);

  // Group options by category (first digit of code usually indicates category)
  const groupedOptions = useMemo(() => {
    const groups = {};

    selectOptions.forEach(option => {
      const category = option.wageType.code.charAt(0);
      const categoryName = getCategoryName(category);

      if (!groups[categoryName]) {
        groups[categoryName] = [];
      }

      groups[categoryName].push(option);
    });

    return Object.keys(groups).map(key => ({
      label: key,
      options: groups[key]
    }));
  }, [selectOptions]);

  // Helper to convert category code to readable name
  function getCategoryName(categoryCode) {
    const categories = {
      '1': 'Lön och ersättning',
      '2': 'Tillägg',
      '3': 'Frånvaro',
      '4': 'Förmåner',
      '5': 'Avdrag',
      '9': 'Övrigt'
    };

    return categories[categoryCode] || `Kategori ${categoryCode}`;
  }

  const handleQuantityChange = (wageTypeId, value) => {
    setWageTypeQuantities(prev => ({
      ...prev,
      [wageTypeId]: parseFloat(value) || 0
    }));
  };

  return (
    <>

      <Modal show={show} onHide={handleClose} size="lg" className="emp-modal">
        <Modal.Header closeButton>
          <Modal.Title>Lägg till lönerader</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <ListToolbar
            search={{
              value: searchQuery,
              onChange: setSearchQuery,
              placeholder: 'Sök löneart eller kod…',
              ariaLabel: 'Sök bland lönearter',
            }}
          />

          <FilterChips
            className="mt-3"
            chips={[
              { key: 'all', label: 'Alla', count: filteredWageTypes.length },
              { key: 'popular', label: 'Vanliga', countless: true },
              { key: '1', label: 'Lön & ersättning', countless: true },
              { key: '3', label: 'Frånvaro', countless: true },
            ]}
            value={showPopularOnly ? 'popular' : selectedCategory}
            onChange={(key) => {
              if (key === 'popular') {
                setShowPopularOnly(true);
                setSelectedCategory('all');
              } else {
                setShowPopularOnly(false);
                setSelectedCategory(key);
              }
            }}
          />

          <div className="wt__list" role="list">
            {filteredWageTypes.length === 0 && (
              <div className="wt__empty">
                {searchQuery ? `Inget matchar "${searchQuery}".` : 'Inga lönearter i det här urvalet.'}
              </div>
            )}
            {groupedOptions.map((group) => (
              <React.Fragment key={group.label}>
                <div className="wt__group">{group.label}</div>
                {group.options.map(({ wageType }) => {
                  const config = getInputConfig(wageType.code);
                  const isSelected = selectedWageTypes.some((wt) => wt.id === wageType.id);
                  const currentQuantity = wageTypeQuantities[wageType.id] ?? config.defaultQuantity;
                  const currentAbsenceDates = absenceDates[wageType.id] || { startDate: null };
                  const toggle = () => {
                    if (isSelected) {
                      setSelectedWageTypes(selectedWageTypes.filter((wt) => wt.id !== wageType.id));
                      const newQuantities = { ...wageTypeQuantities };
                      const newAbsenceDates = { ...absenceDates };
                      delete newQuantities[wageType.id];
                      delete newAbsenceDates[wageType.id];
                      setWageTypeQuantities(newQuantities);
                      setAbsenceDates(newAbsenceDates);
                    } else {
                      setSelectedWageTypes([...selectedWageTypes, wageType]);
                      setWageTypeQuantities({ ...wageTypeQuantities, [wageType.id]: config.defaultQuantity });
                      if (config.requiresAbsenceDates) {
                        setAbsenceDates({ ...absenceDates, [wageType.id]: { startDate: null } });
                      }
                    }
                  };
                  return (
                    <div
                      key={wageType.id}
                      role="listitem"
                      className={`wt__row${isSelected ? ' is-selected' : ''}`}
                      onClick={toggle}
                    >
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={isSelected}
                        onChange={toggle}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`${wageType.code} ${wageType.description}`}
                      />
                      <span className="wt__code">{wageType.code}</span>
                      <span className="wt__name">
                        {wageType.description}
                        {config.description && (
                          <span onClick={(e) => e.stopPropagation()}>
                            <InfoPopover ariaLabel={`Om ${wageType.description}`}>{config.description}</InfoPopover>
                          </span>
                        )}
                      </span>

                      {isSelected && (
                        <div className="wt__controls" onClick={(e) => e.stopPropagation()}>
                          <label className="wt__field">
                            {config.label}
                            <input
                              type="number"
                              min="0"
                              step={config.inputType === 'amount' ? '0.01' : '1'}
                              value={currentQuantity}
                              onChange={(e) => handleQuantityChange(wageType.id, e.target.value)}
                            />
                          </label>
                          {config.quickActions && (
                            <span className="wt__quick" aria-label="Snabbval">
                              {config.quickActions.map((value) => (
                                <button key={value} type="button" onClick={() => handleQuickAction(wageType.id, value)}>
                                  {value}
                                </button>
                              ))}
                            </span>
                          )}
                          {config.requiresAbsenceDates && (
                            <span className={`wt__field${currentAbsenceDates.startDate ? '' : ' is-invalid'}`}>
                              Frånvarodatum
                              <DatePicker
                                selected={currentAbsenceDates.startDate}
                                onChange={(date) => handleAbsenceDateChange(wageType.id, date)}
                                dateFormat="yyyy-MM-dd"
                                placeholderText="Välj datum"
                                portalId="ll-datepicker-portal"
                              />
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>

          {validationErrors.length > 0 && (
            <Callout tone="danger" role="alert" className="mt-3 mb-0">
              <ul className="mb-0 ps-3">
                {validationErrors.map((error, index) => <li key={index}>{error}</li>)}
              </ul>
            </Callout>
          )}
        </Modal.Body>

        <Modal.Footer className="justify-content-between">
          <span className="wt__count">
            {selectedWageTypes.length > 0
              ? `${selectedWageTypes.length} valda: ${selectedWageTypes.map((wt) => wt.code).join(', ')}`
              : 'Ingen löneart vald'}
          </span>
          <div className="d-flex gap-2">
            <button type="button" className="ll-btn ll-btn--secondary" onClick={handleClose}>
              Avbryt
            </button>
            <button
              type="button"
              className="ll-btn ll-btn--primary"
              onClick={() => {
                if (!validateSelection()) {
                  return;
                }

                // Helper function to format date in local timezone (avoid UTC conversion)
                const formatDateLocal = (date) => {
                  if (!date) return null;
                  const year = date.getFullYear();
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const day = String(date.getDate()).padStart(2, '0');
                  return `${year}-${month}-${day}`;
                };

                // Prepare wage types with quantities, input types, and absence dates
                const wageTypesWithData = selectedWageTypes.map(wageType => {
                  const config = getInputConfig(wageType.code);
                  const quantity = wageTypeQuantities[wageType.id] || config.defaultQuantity;
                  const dates = absenceDates[wageType.id];

                  const result = {
                    wage_type_id: wageType.id, // Backend expects wage_type_id
                    code: wageType.code,
                    description: wageType.description,
                    quantity: quantity,
                    inputType: config.inputType
                  };

                  // Add absence dates if required (set both start and end to the same date)
                  if (config.requiresAbsenceDates && dates && dates.startDate) {
                    const formattedDate = formatDateLocal(dates.startDate);
                    result.absenceStartDate = formattedDate;
                    result.absenceEndDate = formattedDate; // Same as start date
                  }

                  return result;
                });

                handleSelect(wageTypesWithData);
                setSelectedWageTypes([]);
                setWageTypeQuantities({});
                setAbsenceDates({});
                setValidationErrors([]);
              }}
              disabled={selectedWageTypes.length === 0}
            >
              <Plus />
              Lägg till{selectedWageTypes.length > 0 ? ` (${selectedWageTypes.length})` : ''}
            </button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SelectWageTypeModal;